import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import * as workTasksAPI from '../../services/workTasksAPI';
import * as projectsAPI from '../../services/projectsAPI';
import { userService } from '../../services/userService';

interface Project {
  id: number;
  name: string;
}

interface User {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
}

const WorkTaskForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'hoch' | 'mittel' | 'niedrig'>('mittel');
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [projectId, setProjectId] = useState<number | ''>('');
  const [assignedToId, setAssignedToId] = useState<number | ''>('');
  const [attachment, setAttachment] = useState<File | null>(null);

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);

  // RBAC Permissions
  const isAdmin = user?.role === 'admin';
  const isBauleiter = user?.role === 'bauleiter';
  const canCreateTasks = isAdmin || isBauleiter;

  useEffect(() => {
    if (!canCreateTasks) {
      navigate('/work-tasks');
      return;
    }

    fetchData();
  }, [canCreateTasks, navigate]);

  useEffect(() => {
    if (isEdit && id) {
      fetchTask();
    }
  }, [isEdit, id]);

  const fetchData = async () => {
    try {
      const [projectsData, usersData] = await Promise.all([
        projectsAPI.getProjects(),
        userService.getUsers(),
      ]);
      setProjects(projectsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching data:', error);
      enqueueSnackbar('Fehler beim Laden der Daten', { variant: 'error' });
    }
  };

  const fetchTask = async () => {
    if (!id) return;

    try {
      setInitialLoading(true);
      const task = await workTasksAPI.getWorkTask(parseInt(id));
      
      setTitle(task.title);
      setDescription(task.description || '');
      setPriority(task.priority);
      setDueDate(task.due_date ? new Date(task.due_date) : null);
      setProjectId(task.project_id);
      setAssignedToId(task.assigned_to_id);
    } catch (error) {
      console.error('Error fetching task:', error);
      enqueueSnackbar('Fehler beim Laden des Arbeitsauftrags', { variant: 'error' });
      navigate('/work-tasks');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      enqueueSnackbar('Titel ist erforderlich', { variant: 'error' });
      return;
    }

    if (!projectId) {
      enqueueSnackbar('Projekt ist erforderlich', { variant: 'error' });
      return;
    }

    if (!assignedToId) {
      enqueueSnackbar('Zugewiesener Benutzer ist erforderlich', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);

      const taskData = {
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        due_date: dueDate ? dueDate.toISOString().split('T')[0] : undefined,
        project_id: projectId as number,
        assigned_to_id: assignedToId as number,
      };

      if (isEdit && id) {
        await workTasksAPI.updateWorkTask(parseInt(id), taskData);
        enqueueSnackbar('Arbeitsauftrag erfolgreich aktualisiert', { variant: 'success' });
      } else {
        await workTasksAPI.createWorkTask(taskData, attachment || undefined);
        enqueueSnackbar('Arbeitsauftrag erfolgreich erstellt', { variant: 'success' });
      }

      navigate('/work-tasks');
    } catch (error) {
      console.error('Error saving task:', error);
      enqueueSnackbar(
        isEdit ? 'Fehler beim Aktualisieren des Arbeitsauftrags' : 'Fehler beim Erstellen des Arbeitsauftrags',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachment(file);
    }
  };

  const getUserDisplayName = (user: User) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.username;
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/work-tasks')}
          >
            Zurück
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Arbeitsauftrag bearbeiten' : 'Neuer Arbeitsauftrag'}
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Titel"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Beschreibung"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    rows={4}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Priorität</InputLabel>
                    <Select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as 'hoch' | 'mittel' | 'niedrig')}
                      disabled={loading}
                    >
                      <MenuItem value="hoch">Hoch</MenuItem>
                      <MenuItem value="mittel">Mittel</MenuItem>
                      <MenuItem value="niedrig">Niedrig</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Fälligkeitsdatum"
                    value={dueDate}
                    onChange={(newValue) => setDueDate(newValue)}
                    disabled={loading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Projekt</InputLabel>
                    <Select
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value as number)}
                      disabled={loading}
                    >
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Zugewiesen an</InputLabel>
                    <Select
                      value={assignedToId}
                      onChange={(e) => setAssignedToId(e.target.value as number)}
                      disabled={loading}
                    >
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {getUserDisplayName(user)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {!isEdit && (
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      disabled={loading}
                    >
                      Datei anhängen
                      <input
                        type="file"
                        hidden
                        onChange={handleFileChange}
                        accept="image/*,.pdf,.doc,.docx"
                      />
                    </Button>
                    {attachment && (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Ausgewählte Datei: {attachment.name}
                      </Typography>
                    )}
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/work-tasks')}
                      disabled={loading}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={20} /> : (isEdit ? 'Aktualisieren' : 'Erstellen')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default WorkTaskForm;
